//@ts-check
import React, {Component} from 'react';
import {bool, func, arrayOf, objectOf, shape, string, object} from 'prop-types';
import classNames from 'classnames';
import {path, pick, values} from 'ramda';
import {default as TextInput} from '@sinclair-digital/sbg-storyline-components/src/components/forms/TextInput';
import {default as Select} from '@sinclair-digital/sbg-storyline-components/src/components/forms/Select';
import {default as Option} from '@sinclair-digital/sbg-storyline-components/src/components/forms/Option';
import {default as Button} from '@sinclair-digital/sbg-storyline-components/src/components/Button';
import {default as Checkbox} from '@sinclair-digital/sbg-storyline-components/src/components/forms/Checkbox';
import {moduleShape as activeModuleShape} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/shapes';
import {moduleShape, pluginShape, pluginRegistryShape} from '@sinclair-digital/sbg-storyline-sdk/lib/node';
import Expando from '../../components/Expando/Expando';
import styles from './PluginManager.scss';


class ModuleControls extends Component {
    static propTypes = {
        className: string,
        ...moduleShape,
        active: bool,
        enabled: bool,
        enableModule: func,
        disableModule: func,
    };

    static defaultProps = {
        // eslint-disable-next-line react/default-props-match-prop-types
        paramList: [],
    };

    onActiveChange = checked =>
        (checked ? this.props.enableModule : this.props.disableModule)({module: this.props.moduleName});

    render() {
        const classList = classNames(styles.ModuleControls, styles.attributeList, this.props.className, {
            [styles.moduleDisabled]: !this.props.enabled,
        });

        return (
            <div className={classList}>
                <h3 className={styles.attributeHeader}>
                    {this.props.enabled && (
                        <Checkbox
                            theme="toggle"
                            className={styles.moduleCheckbox}
                            checked={this.props.active}
                            onChange={this.onActiveChange}
                        />
                    )}
                    {`${this.props.title} (${this.props.moduleName})`}
                </h3>

                <span className={styles.moduleAttributeTitle}>Route:</span>
                <span className={styles.moduleAttribute}>{this.props.route}</span>
                <span className={styles.moduleAttributeTitle}>Route Parameters:</span>
                <span className={styles.moduleAttribute}>{this.props.paramList.join(', ')}</span>
                <span className={styles.moduleAttributeTitle}>Nav:</span>
                <span className={styles.moduleAttribute}>{this.props.showNav ? 'Shown' : 'Hidden'}</span>
                <span className={styles.moduleAttributeTitle}>Header:</span>
                <span className={styles.moduleAttribute}>{this.props.hideHeader ? 'Shown' : 'Hidden'}</span>
            </div>
        );
    }
}

class PluginControls extends Component {
    static propTypes = {
        className: string,
        ...pluginShape,
        author: shape({
            name: string,
            email: string,
            url: string,
        }),
        current: string,
        name: string,
        description: string,
        modules: arrayOf(shape(moduleShape)),
        source: string,
        versions: arrayOf(string),
        activeModules: objectOf(activeModuleShape),
        addLocalOverride: func.isRequired,
        removeLocalOverride: func.isRequired,
        pluginModuleMap: objectOf(string),
        active: string,
        enablePlugin: func.isRequired,
        disablePlugin: func.isRequired,
        enableModule: func.isRequired,
        disableModule: func.isRequired,
    };

    static defaultProps = {
        author: {},
        modules: [],
    };

    state = {localOverrideUrl: ''};

    disablePlugin = () => {
        return this.props.active
            ? this.props.disablePlugin({plugin: this.props.name, version: this.props.current})
            : Promise.resolve();
    };

    enableVersion = e => {
        const version = e.target.value;
        return this.disablePlugin().then(() => this.props.enablePlugin({plugin: this.props.name, version}));
    };

    enableModule = ({module}) => {
        return this.props.enableModule({plugin: this.props.name, version: this.props.current, module});
    };

    disableModule = ({module}) => {
        return this.props.disableModule({plugin: this.props.name, version: this.props.current, module});
    };

    onLocalOverrideUrlChange = ({target: {value}}) => this.setState({localOverrideUrl: value});

    addLocalOverride = () => this.props.addLocalOverride(this.props.name, this.state.localOverrideUrl);

    removeLocalOverride = () => this.props.removeLocalOverride(this.props.name);

    render() {
        const {active, activeModules, className, description, modules, name, source, versions} = this.props;

        const classList = classNames(styles.attributeList, className, {});
        return (
            <div className={classList}>
                <h2 className={styles.attributeHeader}>{`${name} - ${active ? `${active}` : 'disabled'}`}</h2>
                <span className={styles.pluginAttributeTitle}>Desc:</span>
                <span className={styles.pluginAttribute}>{description}</span>
                <span className={styles.pluginAttributeTitle}>Source:</span>
                <span className={styles.pluginAttribute}>{source}</span>
                <span className={styles.pluginAttributeTitle}>Versions:</span>
                <span className={styles.pluginAttribute}>
                    <Select className={styles.versionSelect} theme="v2" value={active} onChange={this.enableVersion}>
                        {versions.map(version => (
                            <Option key={version} theme="v2" value={version}>{version}</Option>
                        ))}
                    </Select>
                    <div className={styles.overrideControls}>
                        <TextInput
                            value={this.state.localOverrideUrl}
                            name="localOverrideUrl"
                            onChange={this.onLocalOverrideUrlChange}
                            placeholder="http://localhost:XXXX"
                        />
                        <Button onClick={this.addLocalOverride}>Activate Local Override</Button>
                        <Button onClick={this.removeLocalOverride}>Remove Override</Button>
                    </div>

                </span>
                <Expando label="Modules">
                    {modules.map(module => (
                        <ModuleControls
                            key={module.moduleName}
                            {...{
                                enabled: !!active,
                                active: !!path([module.moduleName, 'isLive'], activeModules),
                                ...module,
                                enableModule: this.enableModule,
                                disableModule: this.disableModule,
                            }}
                        />
                    ))}
                </Expando>
            </div>
        );
    }
}

export class PluginManager extends Component {
    static propTypes = {
        className: string,
        ...pluginRegistryShape,
        activeModules: objectOf(activeModuleShape),
        pluginModuleMap: objectOf(string),
        active: string,
        addLocalOverride: func.isRequired,
        removeLocalOverride: func.isRequired,
        enablePlugin: func.isRequired,
        disablePlugin: func.isRequired,
        enableModule: func.isRequired,
        disableModule: func.isRequired,
        plugins: object,
    };

    static defaultProps = {
        plugins: {},
    };

    render() {
        const classList = classNames(styles.PluginManager, this.props.className, {});

        return (
            <div className={classList}>
                <h1>Plugins:</h1>
                <div>
                    {values(this.props.plugins).map(plugin => (
                        <PluginControls
                            key={plugin.name}
                            {...plugin}
                            {...{
                                ...pick(Object.keys(PluginControls.propTypes), this.props),
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }
}
